import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Settings, PersonOutline, Close } from '@mui/icons-material';
import { UserOutlined, LockOutlined, LogoutOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import useMenuControl from '../hooks/useMenuControl';
import useAuthData from '../hooks/useAuthData';
import ChangePasswordModal from './modal/EditarSenha';
import EditarCredencial from './modal/EditarCredencial';

const MobileSettingsMenu = ({ isOpen, onClose }) => {
    const { menuItems } = useMenuControl();
    const settingsItem = menuItems.find(item => item.key === '2');
    const settingsChildren = settingsItem ? settingsItem.children : [];

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[99]" onClick={onClose}>
            <div
                className="fixed bottom-0 left-0 right-0 bg-white p-4 rounded-t-2xl shadow-lg"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">Configurações</h3>
                    <button onClick={onClose} className="p-1">
                        <Close className="text-gray-500" />
                    </button>
                </div>
                <nav className="flex flex-col gap-2">
                    {settingsChildren.map(child => (
                        <Link
                            key={child.key}
                            to={child.path}
                            onClick={onClose}
                            className="flex items-center gap-4 p-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            {child.icon}
                            <span>{child.label}</span>
                        </Link>
                    ))}
                </nav>
            </div>
        </div>
    );
};

const MobileProfileMenu = ({ isOpen, onClose, onCredencialClick, onPasswordClick, onLogoutClick }) => {
    const { userData, isLoading, hasPermission } = useAuthData();
    const login_method = localStorage.getItem('login_method');

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[99]" onClick={onClose}>
            <div
                className="fixed bottom-0 left-0 right-0 bg-white p-4 rounded-t-2xl shadow-lg"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">Perfil</h3>
                    <button onClick={onClose} className="p-1">
                        <Close className="text-gray-500" />
                    </button>
                </div>

                <div className="px-4 py-3 border-b border-gray-200 mb-2">
                    <p className="font-semibold text-gray-800 truncate">
                        {isLoading ? <Spin size="small" /> : userData?.nome || 'Usuário'}
                    </p>
                    <p className="text-sm text-gray-500 truncate">{userData?.email}</p>
                </div>

                <nav className="flex flex-col gap-2">
                    {hasPermission('administrador') && (
                        <button
                            onClick={onCredencialClick}
                            className="flex items-center gap-4 p-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-left w-full"
                        >
                            <UserOutlined className="text-lg" />
                            <span>Editar Credencial</span>
                        </button>
                    )}
                    {login_method === '0' && (
                        <button
                            onClick={onPasswordClick}
                            className="flex items-center gap-4 p-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-left w-full"
                        >
                            <LockOutlined className="text-lg" />
                            <span>Alterar Senha</span>
                        </button>
                    )}
                    <button
                        onClick={onLogoutClick}
                        className="flex items-center gap-4 p-3 text-red-500 hover:bg-red-50 rounded-lg transition-colors text-left w-full"
                    >
                        <LogoutOutlined className="text-lg" />
                        <span>Sair</span>
                    </button>
                </nav>
            </div>
        </div>
    );
};

const BottomNav = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
    const [isCredencialModalOpen, setIsCredencialModalOpen] = useState(false);
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const handleProfileMenuClick = (key) => {
        setIsProfileMenuOpen(false);
        switch (key) {
            case 'credencial':
                setIsCredencialModalOpen(true);
                break;
            case 'senha':
                setIsPasswordModalOpen(true);
                break;
            case 'logout':
                localStorage.clear();
                navigate('/login');
                break;
            default:
                break;
        }
    };

    const NavItem = ({ to, icon, label, onClick }) => {
        const isActive = location.pathname === to;

        if (onClick) {
            return (
                <button onClick={onClick} className="flex flex-col items-center justify-center gap-1 w-full h-full">
                    <div className="text-gray-400">{icon}</div>
                    <span className="text-xs text-gray-500">{label}</span>
                </button>
            );
        }

        return (
            <Link to={to} className="flex flex-col items-center justify-center gap-1 w-full h-full">
                <div className={`transition-colors ${isActive ? 'text-alfama-blue' : 'text-gray-400'}`}>{icon}</div>
                <span className={`text-xs transition-colors ${isActive ? 'font-bold text-alfama-blue' : 'text-gray-500'}`}>{label}</span>
            </Link>
        );
    };

    return (
        <>
            <footer className="fixed bottom-0 left-0 right-0 h-16 bg-white border-t border-gray-200 shadow-lg md:hidden z-50">
                <nav className="flex items-center justify-around h-full">
                    <NavItem to="/inicio" label="Início" icon={<Home />} />
                    <NavItem label="Ajustes" icon={<Settings />} onClick={() => setIsMenuOpen(true)} />
                    <NavItem label="Perfil" icon={<PersonOutline />} onClick={() => setIsProfileMenuOpen(true)} />
                </nav>
            </footer>

            <MobileSettingsMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
            <MobileProfileMenu
                isOpen={isProfileMenuOpen}
                onClose={() => setIsProfileMenuOpen(false)}
                onCredencialClick={() => handleProfileMenuClick('credencial')}
                onPasswordClick={() => handleProfileMenuClick('senha')}
                onLogoutClick={() => handleProfileMenuClick('logout')}
            />

            <EditarCredencial isOpen={isCredencialModalOpen} onClose={() => setIsCredencialModalOpen(false)} />
            <ChangePasswordModal isOpen={isPasswordModalOpen} onClose={() => setIsPasswordModalOpen(false)} />
        </>
    );
};

export default BottomNav;